import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, Copy, Gift, Loader2, Percent, X } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { AccountMenu } from "@/components/AccountMenu";

const input = "w-full bg-[#0f0d11] border border-[#352d40] px-3 py-2 text-sm outline-none focus:border-[#D99B43]";
const EMPTY = { code: "", kind: "free_months", percent_off: 50, months: 1, max_redemptions: 10, expires_at: "", note: "" };

const Field = ({ label, children }) => (
  <label className="block">
    <span className="eyebrow block mb-1.5">{label}</span>
    {children}
  </label>
);

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const ExpiryPicker = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const date = value ? new Date(`${value}T12:00:00`) : undefined;
  return (
    <div className="flex gap-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" data-testid="code-expiry-input" className={`${input} flex items-center gap-2 text-left`}>
            <CalendarDays size={14} className="text-stone-500" /> {date ? date.toLocaleDateString() : <span className="text-stone-500">Never</span>}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-[#141018] border-[#5a1e28]" align="start">
          <Calendar
            mode="single"
            selected={date}
            disabled={{ before: new Date(Date.now() + 864e5) }}
            onSelect={(d) => {
              onChange(d ? ymd(d) : "");
              setOpen(false);
            }}
            data-testid="code-expiry-calendar"
          />
        </PopoverContent>
      </Popover>
      {value && (
        <button type="button" onClick={() => onChange("")} className="px-2 text-stone-500 hover:text-stone-200" aria-label="Clear expiry" data-testid="code-expiry-clear"><X size={14} /></button>
      )}
    </div>
  );
};

const CodeForm = ({ onCreated }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k, num) => (e) => setF({ ...f, [k]: num ? Number(e.target.value) : e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const c = await api.createCode({ ...f, code: f.code || null, expires_at: f.expires_at || null });
      onCreated(c);
      setF(EMPTY);
      toast.success(`Code ${c.code} created`);
    } catch (err) {
      toast.error(errMsg(err));
    }
    setBusy(false);
  };
  const discount = f.kind === "discount";
  return (
    <form onSubmit={submit} className="stone-panel p-6 space-y-4" data-testid="code-form">
      <div className="flex gap-2">
        {[["free_months", Gift, "Free months"], ["discount", Percent, "Discount"]].map(([k, Icon, label]) => (
          <button type="button" key={k} data-testid={`code-kind-${k}`} onClick={() => setF({ ...f, kind: k })}
            className={`flex-1 btn-gothic justify-center ${f.kind === k ? "!border-[#D99B43] text-amber-300" : ""}`}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>
      <p className="text-xs text-stone-500">
        {discount ? "Discount codes are entered at card checkout (Stripe). PayPal does not support codes." : "Free-month codes unlock Pro instantly with no card, for free accounts."}
      </p>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Code (blank = random)"><input data-testid="code-input" value={f.code} onChange={set("code")} maxLength={24} placeholder="EARLYFAN" className={`${input} uppercase`} /></Field>
        {discount ? (
          <Field label="Percent off"><input data-testid="code-percent-input" type="number" min={1} max={100} value={f.percent_off} onChange={set("percent_off", true)} className={input} /></Field>
        ) : <div />}
        <Field label={discount ? "Discount lasts (months)" : "Free months"}><input data-testid="code-months-input" type="number" min={1} max={24} value={f.months} onChange={set("months", true)} className={input} /></Field>
        <Field label="Max uses"><input data-testid="code-max-input" type="number" min={1} max={10000} value={f.max_redemptions} onChange={set("max_redemptions", true)} className={input} /></Field>
        <Field label="Expires (optional, end of day)"><ExpiryPicker value={f.expires_at} onChange={(v) => setF({ ...f, expires_at: v })} /></Field>
        <Field label="Note (only you see this)"><input data-testid="code-note-input" value={f.note} onChange={set("note")} maxLength={80} placeholder="For the Discord crew" className={input} /></Field>
      </div>
      <button data-testid="code-create-btn" disabled={busy} className="btn-crimson w-full justify-center">
        {busy && <Loader2 size={14} className="animate-spin" />} Create code
      </button>
    </form>
  );
};

const CodeRow = ({ c, onToggle }) => (
  <tr className="border-t border-[#352d40]" data-testid={`code-row-${c.code}`}>
    <td className="py-3 pr-3">
      <button onClick={() => (navigator.clipboard ? navigator.clipboard.writeText(c.code) : Promise.reject()).then(() => toast.success("Copied"), () => toast(`Code: ${c.code}`))} className="font-mono-vox text-amber-200 flex items-center gap-1.5 hover:text-amber-100" data-testid={`code-copy-${c.code}`}>
        {c.code} <Copy size={12} />
      </button>
      {c.note && <p className="text-[11px] text-stone-500">{c.note}</p>}
    </td>
    <td className="py-3 pr-3 text-sm text-stone-300">{c.kind === "discount" ? `${c.percent_off}% off · ${c.months} mo` : `${c.months} free mo`}</td>
    <td className="py-3 pr-3 text-sm text-stone-300" data-testid={`code-usage-${c.code}`}>{c.redeemed_count} / {c.max_redemptions}</td>
    <td className="py-3 pr-3 text-sm text-stone-400">{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : "Never"}</td>
    <td className="py-3 text-right">
      <button data-testid={`code-toggle-${c.code}`} onClick={() => onToggle(c)} className={`btn-gothic !py-1 !px-3 ${c.active ? "" : "opacity-60"}`}>
        {c.active ? "Active" : "Disabled"}
      </button>
    </td>
  </tr>
);

export default function AdminCodes() {
  const { user } = useAuth();
  const [codes, setCodes] = useState(null);

  useEffect(() => {
    if (user?.role !== "admin") return;
    api
      .adminCodes()
      .then((list) => setCodes((cur) => [...(cur || []).filter((c) => !list.some((x) => x.code === c.code)), ...list]))
      .catch((e) => toast.error(errMsg(e)));
  }, [user]);

  const toggle = async (c) => {
    try {
      const u = await api.setCodeActive(c.code, !c.active);
      setCodes((cur) => cur.map((x) => (x.code === u.code ? { ...x, active: u.active } : x)));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (user?.role !== "admin")
    return <div className="min-h-screen bg-[#080708] flex items-center justify-center text-stone-400" data-testid="admin-forbidden">Admins only. <Link to="/library" className="ml-2 text-amber-300">Back</Link></div>;

  return (
    <div className="min-h-screen bg-[#080708]" data-testid="admin-codes-page">
      <header className="max-w-6xl mx-auto px-6 sm:px-10 h-20 flex items-center gap-6">
        <Link to="/library" className="font-gothic text-lg tracking-[0.15em] text-amber-100/90">VOX <span className="text-[#c92a3e]">✦</span> NOCTURNA</Link>
        <span className="eyebrow text-[#D99B43]">Admin</span>
        <span className="flex-1" />
        <AccountMenu />
      </header>
      <main className="max-w-6xl mx-auto px-6 sm:px-10 pb-20 grid lg:grid-cols-12 gap-10">
        <section className="lg:col-span-5">
          <h1 className="font-gothic text-3xl uppercase tracking-tight text-amber-100/90">Gift & discount codes</h1>
          <p className="font-cormorant italic text-lg text-stone-400 mt-2 mb-6">Share free months of Pro or a discount with friends and early fans.</p>
          <CodeForm onCreated={(c) => setCodes((cur) => [c, ...(cur || [])])} />
        </section>
        <section className="lg:col-span-7">
          <div className="stone-panel p-6 overflow-x-auto">
            {!codes ? (
              <Loader2 className="animate-spin text-[#c92a3e]" />
            ) : codes.length === 0 ? (
              <p className="text-stone-500 text-sm" data-testid="codes-empty">No codes yet. Create your first one.</p>
            ) : (
              <table className="w-full text-left" data-testid="codes-table">
                <thead>
                  <tr className="eyebrow">
                    <th className="pb-2 font-normal">Code</th><th className="pb-2 font-normal">Gives</th><th className="pb-2 font-normal">Used</th><th className="pb-2 font-normal">Expires</th><th />
                  </tr>
                </thead>
                <tbody>{codes.map((c) => <CodeRow key={c.code} c={c} onToggle={toggle} />)}</tbody>
              </table>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
